/**
 * Property ↔ Liberty product assignments (Product Config tab).
 *
 * Product rows: `current_products` on GET /api/property/:id.
 * Catalog (dropdown): Angular uses GET /api/product/active/list (same as Model tab pattern).
 * Embedded `product_list` on property GET is a fallback when the active-list call fails.
 *
 * Persist mappings via `selected_products` on POST /api/property/:id (Angular editProperty).
 * Delete via can_delete_product_mapping → delete_product_mapping.
 */
import apiClient from '@/lib/api/apiClient'
import { CANNOT_DELETE_PRODUCT_MAPPING_MESSAGE, DEFAULT_DELETE_CONFIRM_MESSAGE } from '../constants'
import type {
  PropertyProductCatalogItem,
  PropertyProductRow,
  SelectedProductPayloadItem,
} from '../types'
import { unwrapActiveListPayload } from '../utils/propertyConfigEditLogic'
import { API_ENDPOINTS } from '@/lib/api/config'

export { CANNOT_DELETE_PRODUCT_MAPPING_MESSAGE }

/**
 * Extract the product rows assigned to a property from the property payload.
 * The backend embeds them under `current_products` on GET /api/property/:id.
 * Deduplicates by `property_product_map_id` to avoid rendering the same row twice.
 */
export function extractPropertyProducts(
  payload: Record<string, unknown> | null
): PropertyProductRow[] {
  if (!payload) return []
  const raw = payload.current_products
  if (!Array.isArray(raw)) return []
  const seen = new Set<number | string>()
  const unique: PropertyProductRow[] = []
  for (const item of raw as PropertyProductRow[]) {
    const id = item.property_product_map_id
    if (id != null && seen.has(id as number | string)) continue
    if (id != null) seen.add(id as number | string)
    unique.push(item)
  }
  return unique
}

/**
 * Extract the product catalog from the property payload.
 * The backend embeds it under `product_list` on GET /api/property/:id
 * when the user has `allow_product_config`.
 */
export function extractProductCatalog(
  payload: Record<string, unknown> | null
): PropertyProductCatalogItem[] {
  if (!payload) return []
  const raw = payload.product_list
  if (!Array.isArray(raw)) return []
  return raw as PropertyProductCatalogItem[]
}

/**
 * Angular property-edit `loadProductsList`: GET /api/product/active/list?perPage=100
 * Same pattern as Model tab `fetchActiveModelsList`.
 */
export async function fetchActiveProductsList(): Promise<PropertyProductCatalogItem[]> {
  const { data } = await apiClient.get<{ data?: unknown } | unknown[]>(API_ENDPOINTS.PRODUCT_ACTIVE_LIST, {
    params: { perPage: 100 },
  })
  return unwrapActiveListPayload(data) as PropertyProductCatalogItem[]
}

/**
 * Build Angular `selected_products` JSON for multipart property POST.
 * Skips Base (product_id === 1) and empty product ids; saved rows first, then reversed unsaved.
 * Matches Angular editProperty exactly: only product_id, effective_from, effective_to, and
 * property_product_map_id (for saved rows) — Angular's form group carries no `status`, so none is sent.
 */
export function buildSelectedProductsJson(
  rows: SelectedProductPayloadItem[]
): string | null {
  const saved: SelectedProductPayloadItem[] = []
  const unsaved: SelectedProductPayloadItem[] = []
  for (const row of rows) {
    const productId = Number(row.product_id)
    if (!productId || productId === 1) continue
    const formatted: SelectedProductPayloadItem = {
      product_id: productId,
      effective_from: row.effective_from,
      effective_to: row.effective_to,
    }
    if (row.property_product_map_id != null && Number.isFinite(row.property_product_map_id)) {
      formatted.property_product_map_id = Number(row.property_product_map_id)
      saved.push(formatted)
    } else {
      unsaved.push(formatted)
    }
  }
  const selected = saved.concat(unsaved.reverse())
  return selected.length > 0 ? JSON.stringify(selected) : null
}

export async function deletePropertyProductRow(
  propertyProductMapId: string | number
): Promise<void> {
  const n = Number(String(propertyProductMapId).trim())
  if (!Number.isFinite(n)) {
    throw new Error('Invalid property_product_map_id for delete')
  }
  // Angular: DELETE /api/property/delete_product_mapping/:mapId
  await apiClient.delete(`/api/property/delete_product_mapping/${n}`)
}

function extractApiErrorMessage(err: unknown, fallback: string): string {
  const ax = err as { response?: { data?: unknown }; message?: string }
  const body = ax.response?.data
  if (typeof body === 'string' && body.trim()) return body.trim()
  if (body && typeof body === 'object') {
    const o = body as Record<string, unknown>
    const msg = o.message ?? o.msg ?? o.error ?? o.Message
    if (msg != null && String(msg).trim()) return String(msg).trim()
  }
  if (ax.message && String(ax.message).trim()) return String(ax.message).trim()
  return fallback
}

/**
 * Angular: GET /api/property/can_delete_product_mapping/:mapId before delete.
 * HTTP success → allowed (body is confirm message). HTTP error → blocked.
 */
export async function fetchCanDeleteProductMapping(
  propertyProductMapId: string | number
): Promise<{ allowed: boolean; message?: string }> {
  const n = Number(String(propertyProductMapId).trim())
  if (!Number.isFinite(n)) {
    return { allowed: false, message: 'Invalid product mapping id.' }
  }
  try {
    const { data } = await apiClient.get<unknown>(`/api/property/can_delete_product_mapping/${n}`)
    let message = DEFAULT_DELETE_CONFIRM_MESSAGE
    if (typeof data === 'string' && data.trim()) {
      message = data.trim()
    } else if (data && typeof data === 'object') {
      const o = data as Record<string, unknown>
      const msg = o.message ?? o.msg ?? o.Message
      if (msg != null && String(msg).trim()) message = String(msg).trim()
    }
    return { allowed: true, message }
  } catch (e: unknown) {
    return {
      allowed: false,
      message: extractApiErrorMessage(e, CANNOT_DELETE_PRODUCT_MAPPING_MESSAGE),
    }
  }
}

/**
 * Plain-text (or message-only JSON) responses from GET can_edit_product_mapping.
 * Used for edit gating only (not delete) — matches Angular checkEditProduct.
 */
export function interpretCanEditProductMappingPlainMessage(raw: string): {
  allowed: boolean
  message: string
} {
  const text = raw.trim()
  const lower = text.toLowerCase()
  const blocked =
    lower.includes('so you cannot') ||
    lower.includes('cannot delete the product mapping') ||
    lower.includes('cannot remove the product mapping') ||
    (lower.includes('cannot') &&
      lower.includes('product mapping') &&
      !lower.includes('you can edit'))
  if (blocked) {
    return { allowed: false, message: text }
  }
  return { allowed: true, message: text }
}

/**
 * Normalizes GET /api/property/can_edit_product_mapping/:id response shapes used by Angular.
 */
export function parseCanEditProductMappingResponse(data: unknown): {
  allowed: boolean
  message?: string
} {
  const pickMsg = (v: unknown): string | undefined => {
    if (v == null) return undefined
    const s = String(v).trim()
    return s || undefined
  }

  if (data === true) return { allowed: true }
  if (data === false) {
    return { allowed: false, message: CANNOT_DELETE_PRODUCT_MAPPING_MESSAGE }
  }
  if (data == null || data === '') return { allowed: true }

  if (typeof data === 'string') {
    return interpretCanEditProductMappingPlainMessage(data)
  }

  if (typeof data !== 'object') return { allowed: true }

  const o = data as Record<string, unknown>
  const topMessage =
    pickMsg(o.message) ?? pickMsg(o.msg) ?? pickMsg(o.error) ?? pickMsg(o.Message)

  if ('status' in o) {
    const st = o.status
    const ok =
      st === 1 ||
      st === '1' ||
      st === true ||
      st === 'success' ||
      st === 'ok' ||
      st === 200 ||
      st === '200'
    const bad =
      st === 0 ||
      st === '0' ||
      st === false ||
      st === 'failure' ||
      st === 'error' ||
      st === 'no' ||
      st === 400 ||
      st === '400'
    if (bad) {
      return { allowed: false, message: topMessage ?? CANNOT_DELETE_PRODUCT_MAPPING_MESSAGE }
    }
    if (ok) {
      return topMessage
        ? interpretCanEditProductMappingPlainMessage(topMessage)
        : { allowed: true }
    }
  }

  const boolKeys = ['can_edit', 'canEdit', 'can_delete', 'canDelete', 'allowed', 'editable', 'is_editable']
  for (const k of boolKeys) {
    if (k in o && typeof o[k] === 'boolean') {
      const allowed = o[k] as boolean
      return allowed
        ? topMessage
          ? interpretCanEditProductMappingPlainMessage(topMessage)
          : { allowed: true }
        : { allowed: false, message: topMessage ?? CANNOT_DELETE_PRODUCT_MAPPING_MESSAGE }
    }
  }

  if ('data' in o) {
    const inner = parseCanEditProductMappingResponse(o.data)
    if (!inner.allowed) {
      return {
        allowed: false,
        message: inner.message ?? topMessage ?? CANNOT_DELETE_PRODUCT_MAPPING_MESSAGE,
      }
    }
    if (inner.message) return inner
    return topMessage ? interpretCanEditProductMappingPlainMessage(topMessage) : inner
  }

  if (topMessage) {
    return interpretCanEditProductMappingPlainMessage(topMessage)
  }

  return { allowed: true }
}

/**
 * Angular: GET /api/property/can_edit_product_mapping/:propertyProductMapId for edit gating only.
 */
export async function fetchCanEditProductMapping(
  propertyProductMapId: string | number
): Promise<{ allowed: boolean; message?: string }> {
  const n = Number(String(propertyProductMapId).trim())
  if (!Number.isFinite(n)) {
    return { allowed: false, message: 'Invalid product mapping id.' }
  }
  try {
    await apiClient.get<unknown>(`/api/property/can_edit_product_mapping/${n}`)
    return { allowed: true }
  } catch (e: unknown) {
    return {
      allowed: false,
      message: extractApiErrorMessage(
        e,
        'Could not verify whether this product mapping can be edited.',
      ),
    }
  }
}
